/**
 * Client-side free-text matching for ticket views that filter an already-loaded
 * list in memory (the calendars: monthly / weekly / daily). Case- AND
 * accent-insensitive, so typing "camara flotacion" matches "CÁMARA FLOTACIÓN".
 *
 * The backend-driven lists (tickets / mantenedor / reporte) do NOT use this —
 * they push `search` to Postgres via ticketSearchOr (apps/api). This helper is
 * only for payloads already in the browser (calendar grids have no server-side
 * text search of their own).
 */

// Combining diacritical marks (U+0300–U+036F), stripped after NFD so "Tensión"
// matches "tension". Same rule as the líder dashboard filter.
const DIACRITICS = /[̀-ͯ]/g;

/** Lower-cases and strips diacritics for accent-insensitive matching. */
export function normalize(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(DIACRITICS, '');
}

/**
 * True when `query` is empty (no filter) or `haystack` contains it after
 * normalization. `haystack` is typically the ticket's name + correlativo joined.
 */
export function matchesQuery(haystack: string, query: string): boolean {
  const q = normalize(query.trim());
  if (q === '') return true;
  return normalize(haystack).includes(q);
}

/**
 * Free-text match for a calendar ticket item (monthly / weekly / daily payload).
 * The display name is `planMatriz?.nombre ?? servicio ?? tipoOt?.nombre` — the same
 * label `ticket-card.tsx` renders. `servicio` carries the NPL activity name (those
 * tickets have no planMatriz), so it MUST be in the haystack or the buscador can't
 * find what the card shows. Correlativo and ubicación are folded in too.
 * Structural param, so it works for any item with these fields.
 */
export function ticketMatchesQuery(
  t: {
    correlativo: string;
    planMatriz?: { nombre: string } | null;
    servicio?: string | null;
    tipoOt?: { nombre: string } | null;
    ubicacion?: { nombre: string } | null;
  },
  query: string,
): boolean {
  const haystack = [t.planMatriz?.nombre, t.servicio, t.tipoOt?.nombre, t.correlativo, t.ubicacion?.nombre]
    .filter(Boolean)
    .join(' ');
  return matchesQuery(haystack, query);
}
